"use client"

import React from "react";
import { NextSeo } from 'next-seo';
import { useState, useEffect } from "react"
import Link from "next/link"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  Accordion,
  Table,
  AccordionDetails,
  AccordionSummary,
  Sheet,
  IconButton,
  Drawer,
  List,
  AccordionGroup,
  ListItem,
  ListItemButton,
  Avatar,
} from "@mui/joy"
import {
  Check,
  Star,
  Shield,
  Zap,
  Target,
  BarChart3,
  Calendar,
  MessageSquare,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Users,
  Rocket,
  Award,
  Timer,
  Lock,
  Flame,
  AlertTriangle,
  RefreshCw,
  Menu,
  X,
  Linkedin,
  Clock,
  Bot,
  Lightbulb,
  UserCheck,
  Briefcase,
  Globe,
  FileText,
  BookOpen,
  UploadCloud,
  CalendarCheck,
  BarChart2,
  Type,
  ImagePlay,
  GalleryHorizontalEnd,
  Activity,
  BotMessageSquare,
} from "lucide-react"
import Image from "next/image";
import { useRouter } from "next/router"
import GDPRPopup from '../components/LandingPage/Components/GDPRPopup';
import PropTypes from "prop-types";

export default function ScriptuInspiredLanding() {
  const router = useRouter()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59,
  })

  const faqs = [
    { q: "Do I need to install Scriptu?", a: "No, Scriptu is fully web-based. Just log in and start creating." },
    { q: "Is this really a lifetime deal?", a: "Yes! Pay once and get lifetime access to Scriptu, including all future updates and new features. No monthly fees, no recurring charges, no hidden costs. This is the last lifetime deal we'll ever offer." },
    { q: "Is Scriptu safe to use with LinkedIn?", a: "Absolutely! Scriptu is fully compliant with LinkedIn's terms of service. Your account is safe." },
    { q: "What happens when this deal ends?", a: "After this deal, Scriptu will only be available as a monthly or annual subscription. All lifetime customers will continue to have full access forever with no additional payments required. Don't miss out!" },
    { q: "How many content formats are supported?", a: "Scriptu supports single posts, carousels, hooks, and more." },
    { q: "Can i use for multiple LinkedIn accounts?", a: "Yes, you can use Scriptu with multiple LinkedIn accounts depending on the plan you choose." },
    { q: "Do you offer customer support?", a: "Yes, we provide email support for all customers. For premium plans, we also offer priority support." },
    { q: "Is it compatible with any browser?", a: "Yes, it works on Chrome, Firefox, Safari, and Edge." },
    { q: "Can I try Scriptu for free?", a: "Yes, we offer a free trial. No credit card required." },
    { q: "How do I get support?", a: "You can contact us via email to contact@scriptu.io" },
    { q: "Is there a money-back guarantee?", a: "Absolutely! We offer a 30-day money-back guarantee, no questions asked." },
  ];

  const features = [
    {
      icon: <FileText />,
      title: 'Post from Any Source',
      desc: 'Website, YouTube, docs, or free text — just plug and post.',
    },
    {
      icon: <BookOpen />,
      title: 'AI Hook & CTA Generator',
      desc: 'Grab attention and drive action with persuasive copy.',
    },
    {
      icon: <BarChart2 />,
      title: 'Carousel Builder',
      desc: 'Create stunning multi-slide posts for maximum reach.',
    },
    {
      icon: <Bot />,
      title: 'AI Chat Assistant',
      desc: 'Refine your message with contextual, smart suggestions.',
    },
    {
      icon: <BarChart2 />,
      title: 'Post Strength Analyzer',
      desc: 'Measure readability and impact before posting.',
    },
    {
      icon: <CalendarCheck />,
      title: 'Schedule in Advance',
      desc: 'Set the perfect time and automate your calendar.',
    },
    {
      icon: <Globe />,
      title: 'Multilingual Content',
      desc: 'Generate content in English, French, Spanish, and more.',
    },
    {
      icon: <UploadCloud />,
      title: 'Media Upload & Library',
      desc: 'Use your own visuals or pick from our stock library.',
    },
    {
      icon: <Type />,
      title: 'Brand Voice Control',
      desc: 'Customize tone and format to match your unique voice.',
    },
  ];
  const FeatureCell = ({ value, isPrice = false }) => {
    if (value === '✔') return <Check color="green" />;
    if (value === '✘') return <X color="gray" />;
    return (
      <Typography
        level="body-md"
        sx={{ color: isPrice ? 'success.500' : 'neutral.800', fontWeight: isPrice ? 'lg' : 'md' }}
      >
        {value}
      </Typography>
    );
  };

  FeatureCell.propTypes = {
    value: PropTypes.string.isRequired,
    isPrice: PropTypes.bool,
  };

  const handleCheckout = async (planId) => {
    const url = process.env.NEXT_PUBLIC_BASE_URL + `/checkout?plan=${planId}`;
    router.push(url)
  }

  useEffect(() => {
    const updateTimer = () => {
      const testTime = null; // ← change to `new Date('2025-07-22T16:30:00')` to test 4:30PM
      const now = testTime || new Date()

      const deadline = new Date(now)
      deadline.setHours(23, 30, 0, 0)

      if (now.getTime() > deadline.getTime()) {
        deadline.setDate(deadline.getDate() + 1)
      }

      const distance = deadline.getTime() - now.getTime()

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setTimeLeft({ hours, minutes, seconds })
    }

    const timer = setInterval(updateTimer, 1000)
    updateTimer() // initial run

    return () => clearInterval(timer)
  }, [])


  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
      const viewEventId = `viewcontent_landing_${Date.now()}`

      fetch('/api/facebook-capi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'ViewContent - LTD Landing Page',
          event_source_url: window.location.href,
          content_name: 'Scriptu LTD Plans Landing Page',
          content_type: 'product',
          currency: 'USD',
          value: null,
          event_id: viewEventId,
        }),
      }).catch(console.error)
    }
  }, [])


  return (
    <>
      <NextSeo
        title="Scriptu Lifetime Deal"
        description="Transform your LinkedIn presence with Scriptu's AI-powered tools. Get lifetime access to content generation, scheduling, and optimization features that boost engagement and save time. Limited time offer - pay once, use forever."
        canonical="https://www.scriptu.io/ltd" // Add your canonical URL here
        openGraph={{
          url: "https://www.scriptu.io/ltd", // It's also good to set the URL in openGraph
          title: "Scriptu Lifetime Deal | Transform Your LinkedIn Presence with AI",
          description: "Transform your LinkedIn presence with Scriptu's AI-powered tools. Get lifetime access to content generation, scheduling, and optimization features that boost engagement and save time. Limited time offer - pay once, use forever.",
          images: [
            {
              url: "/logo-promo.png",
              width: 2000,
              height: 520,
              alt: "Scriptu Logo",
            },
          ],
          site_name: "Scriptu",
        }}
        twitter={{
          handle: '@handle',
          site: '@site',
          cardType: 'summary_large_image',
        }}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: '/favicon.png',
          },
          {
            rel: 'apple-touch-icon',
            href: '/favicon.png',
            sizes: '128x128',
          },
        ]}
      />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.body" }}>
        {/* Navigation */}



        <Sheet
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.surface",
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ height: 64 }}>
              <Stack direction="row" alignItems="center" spacing={1}>


                <Link
                  href="/"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none"  // prevents underline
                  }}
                >
                  <Image
                    src="/favicon.png"
                    alt="Scriptu.io"
                    width={128}
                    height={128}
                    style={{ width: "38px", height: "38px" }}
                    priority
                  />
                  <Typography
                    level="h4"
                    fontWeight="bold"
                    sx={{ color: "#232121", ml: 1 }} // ml = margin left for spacing
                  >
                    Scriptu.io
                  </Typography>
                </Link>

              </Stack>

              <Stack direction="row" spacing={4} sx={{ display: { xs: "none", md: "flex" } }}>
                <Link href="/" style={{ textDecoration: "none" }}>
                  <Button variant="plain" color="neutral">
                    Home
                  </Button>
                </Link>
                <Button variant="plain" color="neutral" onClick={() => {
                  const section = document.getElementById("features-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}>
                  Features
                </Button>
                <Button variant="plain" color="neutral" onClick={() => {
                  const section = document.getElementById("pricing-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}>
                  Pricing
                </Button>
                <Button variant="plain" color="neutral" onClick={() => {
                  const section = document.getElementById("reviews-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}>
                  Reviews
                </Button>
                <Button variant="plain" color="neutral" onClick={() => {
                  const section = document.getElementById("faq-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}>
                  FAQ
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ display: { xs: "none", md: "flex" } }}>
                <Button
                  sx={{
                    background: "linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)",
                    color: "white",
                  }}
                  onClick={() => {
                    const section = document.getElementById("pricing-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Buy Now
                </Button>
              </Stack>

              <IconButton
                variant="plain"
                color="neutral"
                sx={{ display: { xs: "flex", md: "none" } }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </IconButton>
            </Stack>
          </Container>

          {/* Mobile Drawer */}
          <Drawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
            <Box sx={{ width: 250, p: 2 }}>
              <List>
                <ListItem>
                  <ListItemButton onClick={() => {
                    const section = document.getElementById("features-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                    setMobileMenuOpen(false);
                  }}>
                    Features
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton
                    onClick={() => {
                      const section = document.getElementById("pricing-section");
                      if (section) section.scrollIntoView({ behavior: "smooth" });
                      setMobileMenuOpen(false);
                    }}
                  >
                    Pricing
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={() => {
                    const section = document.getElementById("reviews-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                    setMobileMenuOpen(false);
                  }}>
                    Reviews
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={() => {
                    const section = document.getElementById("faq-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                    setMobileMenuOpen(false);
                  }}
                  >
                    FAQ
                  </ListItemButton>
                </ListItem>

                <ListItem>
                  <Button
                    fullWidth
                    sx={{
                      background: "linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)",
                      color: "white",
                    }}
                    onClick={() => {
                      const section = document.getElementById("pricing-section");
                      if (section) section.scrollIntoView({ behavior: "smooth" });
                      setMobileMenuOpen(false);
                    }}
                  >
                    Buy Now
                  </Button>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Sheet>

        {/* Announcement Bar */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
            color: "white",
            py: 1.5,
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>

              <Typography level="body-sm" fontWeight="md" color="white">
                🎉 Limited Time: Lifetime Deal (Only 47 spots left)
              </Typography>
              <Chip
                size="sm"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "--Chip-gap": "4px",
                }}
                startDecorator={<Timer size={12} />}
              >
                {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s left
              </Chip>
            </Stack>
          </Container>
        </Box>

        <Sheet
          sx={{
            background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)",
            py: { xs: 3, md: 8 }
          }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

            <Stack spacing={4} alignItems="center" textAlign="center">
              {/*}
              <Chip
                size="lg"
                color="success"
                variant="soft"
                startDecorator={<Infinity size={16} />}
                sx={{
                  px: 3, py: 1, color: "#166534", fontWeight: "600",
                  fontSize: {
                    xs: "0.865rem", // mobile
                    sm: "1.125rem"  // tablet and up
                  },
                  textTransform: "uppercase"
                }}
              >
                LIFETIME DEAL - PAY ONCE, USE FOREVER
              </Chip>
              */}

              <Typography level="h1" sx={{ fontSize: { xs: "2.0rem", md: "4rem" }, fontWeight: "bold", lineHeight: 1.1 }}>
                Go Viral on LinkedIn
                <br />
                <Typography component="span" color="primary" sx={{ fontSize: "inherit" }}>
                  AI-Powered Content That Gets You Noticed
                </Typography>
              </Typography>

              <Typography level="h3" color="neutral" sx={{ maxWidth: "800px", fontWeight: "normal", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
                Generate, Schedule, and Optimize Engaging LinkedIn Posts with AI. Say Goodbye to Creative Blocks and Hello to Consistent Engagement.{" "}
                <Typography component="span" fontWeight="lg" color="black" sx={{ color: "black", fontWeight: "bold", pl: 0.2 }}>
                  No monthly fees. Ever.
                </Typography>
              </Typography>

              <Typography
                level="body-sm"
                color="success"
                sx={{ textAlign: 'center', mt: 2, fontWeight: 'bold' }}
              >
                🔥 {`Limited Offer: Get Scriptu.io Lifetime Deal Before It's Gone!`}
              </Typography>

              {/* Primary CTA */}
              <Stack spacing={2} alignItems="center">
                <Button
                  size="lg"
                  variant="solid"

                  sx={{
                    px: 6,
                    py: 1,
                    backgroundColor: "#f97316",
                    '&:hover': {
                      backgroundColor: "#ea580c",
                    },
                    fontSize: { xs: "1.025rem", md: "1.25rem" },
                    fontWeight: "bold",
                    boxShadow: "lg",
                  }}
                  onClick={() => {
                    const section = document.getElementById("pricing-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Get Lifetime Access - Starting at $59
                </Button>

                <Box mt={4} display="flex" justifyContent="center" flexWrap="wrap" gap={4}>
                  <Typography level="body-md" sx={{ textAlign: 'center', width: '100%' }}>
                    Trusted by 2,847+ professionals
                  </Typography>
                  <Box display="flex" gap={0} alignItems="center" justifyContent="center">
                    <Avatar src="reviews/1.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/3.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/22.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/24.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/27.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/28.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                    <Avatar src="reviews/17.jpeg" alt="AppSumo" variant="outlined" sx={{ border: '4px solid #fff' }} />
                  </Box>
                </Box>

                <Typography level="body-sm" color="neutral">
                  🔒 Secure Payment • ⚡ Instant Access • 💰 30-Day Money-Back Guarantee
                </Typography>
              </Stack>

              {/* Trust Indicators */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Users size={16} color="#3b82f6" />
                  <Typography level="body-sm">2,847+ Happy Users</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Star size={16} color="#eab308" />
                  <Typography level="body-sm">4.9/5 Rating</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Shield size={16} color="#10b981" />
                  <Typography level="body-sm">LinkedIn Compliant</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Sheet>



        <Box
          sx={{
            py: 3,
            background: 'linear-gradient(to right, #2563eb, #9333ea)',
            color: 'white',
          }}
        >
          <Container maxWidth="xl" sx={{ pl: { xs: 2, md: 0 }, pr: { xs: 2, md: 0 } }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left Features */}
              <Grid xs={12} md={4}>
                <Stack spacing={1}>
                  {[
                    'AI-Powered Content Generation',
                    'Schedule Posts in Advance',
                    'Multilingual Support (12 Languages)',
                  ].map((text, i) => (
                    <Stack direction="row" alignItems="center" spacing={1} key={`${text}-${i}`}>
                      <CheckCircle size={20} />
                      <Typography color="white" level="body-md">{text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              {/* Center Pricing */}
              <Grid xs={12} md={4} textAlign="center">
                <Typography color="white" level="h2" sx={{ fontWeight: 'xl', mb: 1 }}>
                  $59 One-Time Payment
                </Typography>
                <Typography color="white" level="body-lg" sx={{ textDecoration: 'line-through', opacity: 0.8 }}>
                  Regular Price: $297/year
                </Typography>
                <Chip
                  size="sm"
                  sx={{
                    mt: 1,
                    bgcolor: '#f97316',
                    color: 'white',
                    fontWeight: 'lg',
                  }}
                >
                  LIFETIME DEAL
                </Chip>
              </Grid>

              {/* Right CTA + Stars */}
              <Grid xs={12} md={4} textAlign="center">
                <Button
                  size="lg"
                  sx={{
                    bgcolor: '#f97316',
                    '&:hover': { bgcolor: 'warning.600' },
                    px: 5,
                    py: 1,
                    fontWeight: 'xl',
                    fontSize: 'lg',
                  }}
                  onClick={() => {
                    const section = document.getElementById("pricing-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Get Lifetime Access Now
                </Button>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5} mt={2}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} style={{ fill: '#facc15', color: '#facc15' }} />
                  ))}
                  <Typography color="white" level="body-sm" sx={{ ml: 1 }}>
                    4.9/5 (127 reviews)
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>



        {/* Features Section */}
        <Box sx={{ py: 10, bgcolor: "background.level1" }} id="features-section">
          <Container maxWidth="xl">
            <Stack spacing={8} alignItems="center">
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography level="h2" fontWeight="bold">
                  Everything you need to dominate LinkedIn
                </Typography>
                <Typography level="h4" sx={{ color: "text.secondary", maxWidth: "600px" }}>
                  Stop spending hours on content creation. Let Scriptu do the heavy lifting while you focus on what matters.
                </Typography>
              </Stack>

              <Grid container spacing={4}>
                {[
                  {
                    icon: <Bot size={24} />,
                    title: "AI Content Generation",
                    description: "Create Linkedin posts that match your brand's voice, tailored to your audience and industry.",
                    features: ["Industry-specific content", "Personal voice matching", "Customized post formats"],
                    color: "#9333ea",
                  },
                  {
                    icon: <Rocket size={24} />,
                    title: "Post from multiple sources",
                    description: "Easily create posts from websites, YouTube videos, documents, or free text.",
                    features: ["Website Content", "YouTube videos", "Document uploads", "Free text input"],
                    color: "#dc2626",
                  },
                  {
                    icon: <Calendar size={24} />,
                    title: "Schedule your posts in advance",
                    description: "Plan your content calendar and manage posting at optimal times.",
                    features: ["Schedule posts", "Content calendar management", "Optimal posting times"],
                    color: "#2563eb",
                  },
                  {
                    icon: <BarChart3 size={24} />,
                    title: "Post Strength Analyzer",
                    description: "Measure readability, engagement potential, and content quality before posting.",
                    features: ["Readability score", "Engagement prediction", "Content improvement suggestions"],
                    color: "#10b981",
                  },
                  {
                    icon: <GalleryHorizontalEnd size={24} />,
                    title: "Linkedin Carousel Builder",
                    description: "Create stunning multi-slide posts that boost visibility and engagement.",
                    features: ["Drag-and-drop interface", "Customize colors and fonts", "Add images", "Generate from multiple sources"],
                    color: "#4f46e5",
                  },

                  {
                    icon: <Zap size={24} />,
                    title: "AI Hook & CTA Generator",
                    description: "Craft compelling hooks and calls-to-action that drive clicks and engagement.",
                    features: ["Attention-grabbing hooks", "Actionable CTAs", "A/B testing suggestions"],
                    color: "#f59e0b",
                  },

                  {
                    icon: <Target size={24} />,
                    title: "Audience Targeting",
                    description: "Create content that resonates with your specific industry audience using AI insights.",
                    features: ["Industry-specific targeting", "Audience behavior analysis", "Content personalization"],
                    color: "#f59e0b",
                  },
                  {
                    icon: <BotMessageSquare size={24} />,
                    title: "AI Chat Assistant",
                    description: "Chat with your posts to refine and improve them with AI suggestions.",
                    features: ["Contextual suggestions", "Real-time editing", "Content refinement"],
                    color: "#3d1b7cff",
                  },
                  {
                    icon: <Globe size={24} />,
                    title: "Multilingual Content",
                    description: "Generate posts in English, French, Spanish, Polish and more, over 12 languages supported.",
                    features: ["12+ languages supported", "Automatic translation", "Localized content generation"],
                    color: "#4f46e5",
                  },
                  {
                    icon: <ImagePlay size={24} />,
                    title: "Media Upload & Library",
                    description: "Easily upload images, videos, and documents to enhance your posts.",
                    features: ["Unlimited media uploads", "Stock image library", "Stock video library", "GIF library", "AI-generated images"],
                    color: "#8b5cf6",
                  },
                  {
                    icon: <Activity size={24} />,
                    title: "Viral Post Database",
                    description: "Access a database of 10,000+ high-performing posts for inspiration and ideas.",
                    features: ["Trending post examples", "Industry-specific posts", "Content inspiration"],
                    color: "#dc2626",
                  },
                  {
                    icon: <Linkedin size={24} />,
                    title: "LinkedIn Compliance",
                    description:
                      "100% compliant with LinkedIn's terms of service. Safe, secure, and approved automation.",
                    features: ["Official LinkedIn API", "Terms of service compliant", "Account safety guaranteed"],
                    color: "#6366f1",
                  },
                ].map((feature, index) => (
                  <Grid xs={12} sm={6} lg={4} key={`${feature.title}-${index}`} sx={{ mb: 2 }}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "none",
                        boxShadow: "lg",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "xl",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: `${feature.color}15`,
                              borderRadius: "xl",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: feature.color,
                            }}
                          >
                            {feature.icon}
                          </Box>
                          <Typography level="h4" fontWeight="bold">
                            {feature.title}
                          </Typography>
                          <Typography level="body-md" sx={{ color: "text.secondary" }}>
                            {feature.description}
                          </Typography>
                          <Stack spacing={1}>
                            {feature.features.map((item, idx) => (
                              <Stack direction="row" alignItems="center" spacing={1} key={`${item}-${idx}`}>
                                <CheckCircle size={16} color="#10b981" />
                                <Typography level="body-sm">{item}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        {/* Problem/Pain Point */}
        <Box sx={{ py: 8 }}>
          <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>
            <Stack spacing={6} alignItems="center">
              <Stack spacing={3} alignItems="center" textAlign="center">
                <Typography level="h1" fontWeight="bold">
                  Tired of Struggling with LinkedIn Content?
                </Typography>
                <Typography level="h4" color="neutral">
                  {`You're not alone. Most professionals face these same challenges every day:`}
                </Typography>
              </Stack>

              <Grid container spacing={4} sx={{ maxWidth: "1000px", pb: 3.5 }}>
                <Grid xs={12} md={4}>
                  <Card variant="soft" sx={{
                    p: 3, textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderColor: "#fecaca", borderWidth: 1, borderStyle: "solid"
                  }}>
                    <Stack spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: "danger.100",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Clock size={32} color="#dc2626" />
                      </Box>
                      <Typography level="h4" color="danger" fontWeight="bold">
                        Time Consuming
                      </Typography>
                      <Typography color="neutral">
                        Spending 2-3 hours daily creating LinkedIn content with little to show for it
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>

                <Grid xs={12} md={4}>
                  <Card variant="soft" sx={{
                    p: 3, textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderColor: "#fecaca", borderWidth: 1, borderStyle: "solid"
                  }}>
                    <Stack spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: "danger.100",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MessageSquare size={32} color="#dc2626" />
                      </Box>
                      <Typography level="h4" color="danger" fontWeight="bold">
                        Low Engagement
                      </Typography>
                      <Typography color="neutral">
                        Posts getting 2-5 likes while competitors get hundreds of interactions
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>

                <Grid xs={12} md={4}>
                  <Card variant="soft" sx={{
                    p: 3, textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderColor: "#fecaca", borderWidth: 1, borderStyle: "solid"
                  }}>
                    <Stack spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: "danger.100",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TrendingUp size={32} color="#dc2626" />
                      </Box>
                      <Typography level="h4" color="danger" fontWeight="bold">
                        Missed Opportunities
                      </Typography>
                      <Typography color="neutral">
                        Losing potential clients and partnerships due to weak LinkedIn presence
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>

              <Button size="lg" variant="solid"
                onClick={() => {
                  const section = document.getElementById("pricing-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}
                sx={{
                  px: 4, py: 0, fontSize: "1.125rem", backgroundColor: "#f97316",
                  marginTop: "0px !important",
                  '&:hover': {
                    backgroundColor: "#ea580c",
                  },
                }}>
                Solve This Problem Now
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Comparison Table Section */}
        <Box sx={{ py: 10, bgcolor: 'background.body' }}>
          <Container maxWidth="xl">
            <Typography level="h1" textAlign="center" sx={{ mb: 1 }}>
              Why Choose Scriptu.io?
            </Typography>
            <Typography level="body-md" textAlign="center" sx={{ mb: 4 }}>
              See how we stack up against the competition
            </Typography>

            {/* Scrollable container */}
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                borderAxis="xBetween"
                size="lg"
                variant="soft"
                sx={{
                  minWidth: '700px', // set to minimum table width or based on columns
                  '& thead th': {
                    fontWeight: 'lg',
                    backgroundColor: '#f9fafb',
                    px: 1.5,
                    pt: 0,
                    pb: 2,
                    border: '0px solid transparent',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#f9fafb',
                  },
                  '& tbody tr:nth-of-type(odd)': {
                    backgroundColor: '#ffffff',
                  },
                  '& td, & th': {
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap', // prevent long text from wrapping
                  },
                  '& td:first-of-type, & th:first-of-type': {
                    textAlign: 'left',
                    whiteSpace: 'normal',
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th><span style={{ color: '#2563eb', fontWeight: 'bold' }}>Scriptu.io</span></th>
                    <th>Hootsuite</th>
                    <th>Buffer</th>
                    <th>Sprout Social</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>AI Content Generation</td>
                    <td><FeatureCell value="✔" /></td>
                    <td><FeatureCell value="✘" /></td>
                    <td><FeatureCell value="✘" /></td>
                    <td><FeatureCell value="✘" /></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>LinkedIn Optimization</td>
                    <td><FeatureCell value="✔" /></td>
                    <td><FeatureCell value="✔" /></td>
                    <td><FeatureCell value="✔" /></td>
                    <td><FeatureCell value="✔" /></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Lifetime Deal</td>
                    <td><FeatureCell value="✔" /></td>
                    <td><FeatureCell value="✘" /></td>
                    <td><FeatureCell value="✘" /></td>
                    <td><FeatureCell value="✘" /></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Monthly Cost</td>
                    <td><FeatureCell value="$0" isPrice /></td>
                    <td><FeatureCell value="$99/mo" /></td>
                    <td><FeatureCell value="$120/mo" /></td>
                    <td><FeatureCell value="$249/mo" /></td>
                  </tr>
                </tbody>
              </Table>
            </Box>
          </Container>
        </Box>



        {/* Testimonials Section */}
        <Box sx={{ py: 10, bgcolor: "background.level1" }} id="reviews-section">
          <Container maxWidth="xl">
            <Stack spacing={8} alignItems="center">
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography level="h2" fontWeight="bold">
                  Loved by 2,847+ professionals
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} fill="#f59e0b" color="#f59e0b" />
                  ))}
                  <Typography level="body-lg" fontWeight="bold" sx={{ ml: 1 }}>
                    4.9 out of 5
                  </Typography>
                </Stack>
              </Stack>

              <Grid container spacing={4}>
                {[
                  {
                    name: "Audrey Robbins",
                    avatar: "/reviews/7.jpeg",
                    testimonial:
                      "Scriptu.io completely transformed my LinkedIn presence. I went from 2-3 likes per post to averaging 50+ likes and 10+ comments. The AI really understands my industry!",
                  },
                  {
                    name: "Michael Tôrnqvist",
                    avatar: "/reviews/1.jpeg",
                    testimonial:
                      "I was spending 2 hours daily on LinkedIn content. Now it takes me 15 minutes to schedule a week's worth of posts. The ROI is incredible - I've generated 23 qualified leads this month!",
                  },
                  {
                    name: "Emily Mctaggart",
                    avatar: "/reviews/8.jpeg",
                    testimonial:
                      "The content quality is amazing. People constantly ask if I hired a professional writer. My follower count has grown from 500 to 8,000 in just 4 months!",
                  },
                  {
                    name: "Matthieu Thibault",
                    avatar: "/reviews/9.jpeg",
                    testimonial:
                      "I love the AI chat assistant. It helps me refine my posts in real-time and gives me suggestions I never would have thought of. My engagement has skyrocketed!",
                  },
                  {
                    name: "Aisha Khan",
                    avatar: "/reviews/22.jpeg",
                    testimonial:
                      "Scriptu.io is a game changer for freelancers like me. I can now focus on delivering value to my clients while Scriptu handles my LinkedIn content. My visibility has never been better!",
                  },
                  {
                    name: "Marina Petrychenko",
                    avatar: "/reviews/31.jpeg",
                    testimonial:
                      "The multilingual support is fantastic. I can now post in both English and Ukrainian, reaching a wider audience. The AI-generated content is top-notch!",
                  },
                  {
                    name: "Tobias Grahn",
                    avatar: "/reviews/4.jpeg",
                    testimonial: "Honestly didn’t expect much, but Scriptu blew me away. I wrote a few prompts and it gave me full posts that sounded like me — not some robotic nonsense."
                  },
                  {
                    name: "Lina Kowalska",
                    avatar: "/reviews/10.jpeg",
                    testimonial: "I’ve tried a bunch of these LinkedIn tools, but this one finally clicked. It doesn’t overdo it. I just tell it what I’m thinking, and it turns it into solid content I actually post."
                  },
                  {
                    name: "Gerard Mendez",
                    avatar: "/reviews/3.jpeg",
                    testimonial: "I used to skip posting for weeks. Now I bulk-write on Sundays with Scriptu and schedule the whole week. Makes me look way more active than I am, lol."
                  },
                  {
                    name: "Nadia Farouk",
                    avatar: "/reviews/19.jpeg",
                    testimonial: "It’s not perfect, but man it saves me hours. I used to stress over LinkedIn — now it’s kind of fun to mess around with ideas and let the AI clean them up."
                  },
                  {
                    name: "João Carreira",
                    avatar: "/reviews/17.jpeg",
                    testimonial: "Scriptu helped me go from 300 views to over 5k per post. No gimmicks, just consistent content that feels like me, but better written."
                  },
                  {
                    name: "Mireille Aubry",
                    avatar: "/reviews/11.jpeg",
                    testimonial: "What I like most is how natural the writing feels. It’s not trying too hard. I still tweak things, but 80% of the job is already done."
                  }
                ].map((testimonial, index) => (
                  <Grid xs={12} lg={4} key={`${testimonial.name}-${index}`} sx={{ mb: 2 }}>
                    <Card sx={{ height: "100%", border: "none", boxShadow: "lg" }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={3}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                            ))}
                            <Chip size="sm" sx={{ bgcolor: "#dcfce7", color: "#166534" }}>
                              VERIFIED
                            </Chip>
                          </Stack>

                          <Typography level="body-md" sx={{ color: "text.secondary" }}>
                            {'"'}
                            {testimonial.testimonial}
                            {'"'}
                          </Typography>

                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar src={testimonial.avatar} size="md" />
                            <Stack>
                              <Typography level="body-md" fontWeight="bold">
                                {testimonial.name}
                              </Typography>

                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                {testimonial.metricIcon}
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Button
                size="lg"
                endDecorator={<ArrowRight size={20} />}
                sx={{
                  background: "linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)",
                  color: "white",
                }}
                onClick={() => {
                  const section = document.getElementById("pricing-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Join 2,847+ Happy Users
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Who is it for Section */}
        <Box sx={{ py: 10, bgcolor: "background.level1" }}>
          <Container maxWidth="xl">
            <Stack spacing={8} alignItems="center">
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography level="h2" fontWeight="bold">
                  Who is Scriptu.io for?
                </Typography>
                <Typography level="h4" sx={{ color: "text.secondary", maxWidth: "600px" }}>
                  {`Whether you're a freelancer, small business owner, or corporate professional, Scriptu.io is designed to
                                supercharge your LinkedIn presence.`}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {[
                  {
                    icon: <UserCheck size={24} />,
                    title: "Freelancers",
                    description:
                      "Stand out on LinkedIn with consistent, high-quality posts that build trust and authority in your niche.",
                    color: "#9333ea",
                  },
                  {
                    icon: <Briefcase size={24} />,
                    title: "Small Business Owners",
                    description:
                      "Grow your online presence and connect with your ideal audience — without spending hours writing content.",
                    color: "#2563eb",
                  },
                  {
                    icon: <Users size={24} />,
                    title: "Corporate Professionals",
                    description:
                      "Position yourself as a thought leader in your industry with polished, insightful content — all done for you.",
                    color: "#10b981",
                  },
                  {
                    icon: <Globe size={24} />,
                    title: "Agencies",
                    description:
                      "Easily manage multiple client LinkedIn accounts with team collaboration, scheduling, and automation tools.",
                    color: "#f59e0b",
                  },
                  {
                    icon: <Target size={24} />,
                    title: "Content Creators",
                    description:
                      "Keep your audience engaged with fresh ideas, carousels, and hooks — powered by AI that understands your voice.",
                    color: "#dc2626",
                  },
                  {
                    icon: <Shield size={24} />,
                    title: "Marketers",
                    description:
                      "Streamline your LinkedIn strategy with content that's consistent, on-brand, and tailored to your audience.",
                    color: "#6366f1",
                  },
                ].map((item, index) => (
                  <Grid xs={12} sm={6} lg={4} key={`${item.title}-${index}`} sx={{ mb: 2 }}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "none",
                        boxShadow: "lg",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "xl",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: `${item.color}15`,
                              borderRadius: "xl",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: item.color,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography level="h4" fontWeight="bold">
                            {item.title}
                          </Typography>
                          <Typography level="body-md" sx={{ color: "text.secondary" }}>
                            {item.description}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </Box>

        {/* Pricing Section */}
        <Box sx={{ pt: 10 }} id="pricing-section">
          <Container maxWidth="xl">
            <Stack spacing={8} alignItems="center">
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Chip startDecorator={<Flame size={16} />} sx={{ bgcolor: "#fef2f2", color: "#dc2626" }}>
                  Limited Time Offer
                </Chip>
                <Typography level="h2" fontWeight="bold">
                  Choose your lifetime plan
                </Typography>
                <Typography level="h4" sx={{ color: "text.secondary" }}>
                  One-time payment. No recurring fees. Own it for life.
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    bgcolor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "lg",
                  }}
                >
                  <AlertTriangle size={16} color="#dc2626" style={{ marginRight: 8 }} />
                  <Typography level="body-sm" sx={{ color: "#dc2626", fontWeight: "md" }}>
                    Only 47 licenses remaining at this price
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={4} sx={{ maxWidth: "1200px" }}>
                {[
                  {
                    name: "Lifetime Deal",
                    badge: "STARTER",
                    badgeColor: "#2563eb",
                    price: "$59",
                    originalPrice: "Normally $216/year",
                    savings: "Save 73%",
                    features: [
                      "All Features above included",
                      "50,000 words per month",
                      "3 team members",
                      "10 Ai images per month",
                      "Email Support",
                    ],
                    buttonText: "Buy now (Standard Plan)",
                    buttonColor: "#2563eb",
                    description: "Perfect for individuals",
                    popular: false,
                    planId: "standard",
                  },
                  {
                    name: "Lifetime Deal",
                    badge: "PROFESSIONAL",
                    badgeColor: "#9333ea",
                    price: "$118",
                    originalPrice: "Normally $348/year",
                    savings: "Save 66%",
                    features: [
                      "All Features above included",
                      "125,000 words per month",
                      "6 team members",
                      "25 Ai images per month",
                      "Email Support",
                    ],
                    buttonText: "Buy now (Pro Plan)",
                    buttonColor: "#9333ea",
                    description: "Best for growing businesses",
                    popular: true,
                    planId: "pro",
                  },
                  {
                    name: "Lifetime Deal",
                    badge: "AGENCY",
                    badgeColor: "#f59e0b",
                    price: "$177",
                    originalPrice: "Normally $588/year",
                    savings: "Save 70%",
                    features: [
                      "All Features above included",
                      "250,000 words per month",
                      "20 team members",
                      "50 Ai images per month",
                      "Email Support",
                    ],
                    buttonText: "Buy now (Agency Plan)",
                    buttonColor: "#f59e0b",
                    description: "Perfect for agencies",
                    popular: false,
                    planId: "agency",
                  },
                ].map((plan, index) => (
                  <Grid xs={12} lg={4} key={`${plan.name}-${index}`} sx={{ mb: 8 }}>
                    <Card
                      sx={{
                        height: "100%",
                        border: plan.popular ? `2px solid ${plan.badgeColor}` : "2px solid transparent",
                        boxShadow: plan.popular ? "xl" : "lg",
                        transform: plan.popular ? "scale(1.05)" : "none",
                        position: "relative",
                      }}
                    >
                      {Boolean(plan && plan.popular) && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -16,
                            left: "50%",
                            transform: "translateX(-50%)",
                          }}
                        >
                          <Chip sx={{ bgcolor: plan.badgeColor, color: "white", fontWeight: "bold" }}>MOST POPULAR</Chip>
                        </Box>
                      )}

                      <CardContent sx={{ p: 3, textAlign: "center" }}>
                        <Stack spacing={3}>
                          <Stack spacing={1}>
                            <Chip
                              size="sm"
                              sx={{
                                bgcolor: `${plan.badgeColor}15`,
                                color: plan.badgeColor,
                                alignSelf: "center",
                              }}
                            >
                              {plan.badge}
                            </Chip>
                            <Typography level="h3" fontWeight="bold">
                              {plan.name}
                            </Typography>
                          </Stack>

                          <Stack spacing={1}>
                            <Typography level="h1" fontWeight="bold">
                              {plan.price}
                            </Typography>
                            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                              <Box component="span" sx={{ textDecoration: "line-through" }}>
                                {plan.originalPrice}
                              </Box>
                            </Typography>
                            <Typography level="body-sm" sx={{ color: "#10b981", fontWeight: "bold" }}>
                              {plan.savings}
                            </Typography>
                          </Stack>

                          <Stack spacing={2}>
                            {plan.features.map((feature, idx) => (
                              <Stack direction="row" alignItems="center" spacing={1} key={`${feature}-${idx}`} sx={{ textAlign: "left" }}>
                                <Check size={20} color="#10b981" />
                                <Typography level="body-md">{feature}</Typography>
                              </Stack>
                            ))}
                          </Stack>

                          <Button
                            fullWidth
                            sx={{
                              bgcolor: plan.buttonColor,
                              color: "white",
                              py: 1.5,
                              fontWeight: "bold",
                              "&:hover": {
                                bgcolor: plan.buttonColor,
                                opacity: 0.9,
                              },
                            }}
                            onClick={() => handleCheckout(plan.planId)}
                          >
                            {plan.buttonText}
                          </Button>

                          <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                            {plan.description}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>


              <Typography level="body-md" sx={{ color: "text.secondary", textAlign: "center", marginTop: "0rem !important" }}>
                All plans include:
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "1rem !important", gap: 2, flexWrap: "wrap" }}>
                {[
                  { icon: <Shield color="#22c55e" size={16} />, text: "30-Day Money-Back Guarantee" },
                  { icon: <RefreshCw color="#3b82f6" size={16} />, text: "No Recurring Fees" },
                  { icon: <Zap size={16} color="#eab308" />, text: "Instant Access" },
                  { icon: <Award size={16} color="#a855f7" />, text: "Lifetime Updates" },
                ].map((item, index) => (
                  <Stack direction="row" alignItems="center" spacing={0.5} key={`${item.text}-${index}`} sx={{ textAlign: "center" }}>
                    {item.icon}
                    <Typography level="body-sm" color="neutral.600">{item.text}</Typography>
                  </Stack>
                ))}
              </Box>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 3,
                  py: 2,
                  marginTop: "2rem !important",
                  bgcolor: "#fffbeb",
                  border: "1px solid #fed7aa",
                  borderRadius: "lg",
                }}
              >
                <Lightbulb size={20} color="#f59e0b" style={{ marginRight: 8 }} />
                <Typography level="body-sm" sx={{ color: "#92400e", fontWeight: "md" }}>
                  💡 Pro tip: 73% choose Professional Plan for the best value. Upgrade anytime by paying the difference.
                </Typography>
              </Box>

            </Stack>
          </Container>
        </Box>

        {/* Features Section */}
        <Sheet sx={{ bgcolor: "f9fafb", py: 6 }}>
          <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2, textAlign: 'center' }}>
            <Typography level="h2" fontSize="2rem" fontWeight="lg" sx={{ mb: 2 }}>
              Why 2,847+ Creators Trust Scriptu.io
            </Typography>
            <Typography level="body-lg" sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
              {`Scriptu isn't just another AI tool. It's a complete LinkedIn content engine — designed to save time, get engagement, and help you go viral in your niche.`}
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {features.map((f, i) => (
                <Grid key={`${f.title}-${i}`} xs={12} sm={6} md={4} sx={{ mb: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontSize: 40, color: 'primary.600', mb: 1 }}>{f.icon}</Box>
                      <Typography level="h4" fontWeight="lg" sx={{ mb: 1 }}>
                        {f.title}
                      </Typography>
                      <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                        {f.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Sheet>

        {/* Money-Back Guarantee */}
        <Box sx={{ py: 10, bgcolor: "#f0fdf4" }}>
          <Container maxWidth="lg">
            <Stack spacing={4} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "#dcfce7",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={40} color="#10b981" />
              </Box>

              <Typography level="h2" fontWeight="bold">
                30-Day Money-Back Guarantee
              </Typography>

              <Card sx={{ maxWidth: "800px", boxShadow: "xl", border: "none" }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack spacing={4}>
                    <Typography level="h4" sx={{ color: "text.secondary" }}>
                      {`Try Scriptu.io completely risk-free for 30 days. If you don't see a significant improvement in your
                                        LinkedIn engagement and growth, we'll refund every penny. No questions asked.`}
                    </Typography>

                    <Grid container spacing={4}>
                      <Grid xs={12} md={6}>
                        <Stack spacing={2} alignItems="flex-start">
                          <Typography level="body-lg" fontWeight="bold">
                            {`What's Guaranteed:`}
                          </Typography>
                          <Stack spacing={1.5}>
                            {["Noticeable increase in post reach and engagement", "Save 10+ hours per week", "Attract more relevant leads and profile visits"].map(
                              (item, idx) => (
                                <Stack direction="row" alignItems="center" spacing={1} key={`${item}-${idx}`} sx={{ textAlign: "left" }}>
                                  <CheckCircle size={20} color="#10b981" />
                                  <Typography level="body-md">{item}</Typography>
                                </Stack>
                              ),
                            )}
                          </Stack>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={2} alignItems="flex-start">
                          <Typography level="body-lg" fontWeight="bold">
                            How It Works:
                          </Typography>
                          <Stack spacing={1.5}>
                            {["Email us within 30 days", "Full refund in 2-3 days", "Keep all generated content"].map(
                              (item, idx) => (
                                <Stack direction="row" alignItems="center" spacing={1} key={`${item}-${idx}`}>
                                  <CheckCircle size={20} color="#10b981" />
                                  <Typography level="body-md">{item}</Typography>
                                </Stack>
                              ),
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>

              <Button
                size="lg"
                startDecorator={<Shield size={20} />}
                sx={{
                  bgcolor: "#10b981",
                  color: "white",
                  px: 6,
                  py: 2,
                  fontSize: "1.125rem",
                  fontWeight: "bold",
                }}
                onClick={() => {
                  const section = document.getElementById("pricing-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Start Risk-Free Trial
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ px: 2, py: { xs: 6, md: 10 }, display: "flex", justifyContent: "center" }} id="faq-section">
          <Sheet
            variant="soft"
            sx={{
              maxWidth: "1200px",
              width: "100%",
              borderRadius: "lg",
              p: { xs: 3, md: 15 },
              bgcolor: "#09002b", // very dark indigo background (custom)
              color: "common.white",
            }}
          >
            <Typography
              level="h1"
              textAlign="center"
              fontWeight="lg"
              sx={{ mb: 6, color: 'white' }}
            >
              Frequently Asked
              <Box
                component="span"
                sx={{
                  position: "relative",
                  display: "inline-block",
                  pl: 1,
                  pb: 0.5,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "10px",
                    bottom: 0,
                    width: "100%",
                    height: "3px", // underline thickness
                    background: "linear-gradient(90deg, #9333ea, #2563eb)",
                    borderRadius: "2px",
                  },
                }}
              >
                Questions
              </Box>
            </Typography>

            <AccordionGroup
              variant="plain"
              sx={{
                borderRadius: "md",
                "--joy-palette-text-primary": "#fff",
                "--joy-palette-neutral-plainHoverBg": "rgba(255,255,255,0.05)",
                "--joy-palette-neutral-plainActiveBg": "rgba(255,255,255,0.1)", // override active background
              }}
            >
              {faqs.map((item, i) => (
                <Accordion
                  key={`faq-${i}`}
                  sx={{
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    '&[aria-expanded="true"]': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <AccordionSummary sx={{ p: 2 }}>
                    <Typography level="title-md" sx={{ color: "white", mt: 2, mb: 2, '&:hover': { color: 'white' } }}>
                      {item.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography level="body-sm" sx={{ color: "neutral.300", p: 2 }}>
                      {item.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionGroup>
          </Sheet>
        </Box>


        <Box sx={{ px: 2, py: { xs: 6, md: 10 }, display: "flex", justifyContent: "center" }} id="contact-section">
          <Sheet
            variant="soft"

          >
            <Card variant="soft" color="primary" sx={{ p: 3, maxWidth: "600px", textAlign: "center" }}>
              <Stack spacing={3}>
                <Typography level="h4" fontWeight="bold">
                  Still Have Questions?
                </Typography>
                <Typography color="neutral">
                  {`Our support team is here to help. Email us at contact@scriptu.io and we'll respond within 24 hours.`}
                </Typography>
                <Button size="sm" color="warning" variant="solid"
                  onClick={() => {
                    const section = document.getElementById("pricing-section");
                    if (section) section.scrollIntoView({ behavior: "smooth" });
                  }}
                  sx={{ px: 4, py: 1, fontSize: "1.125rem", backgroundColor: "#f97316", '&:hover': { backgroundColor: "#ea580c" } }}>
                  Get Scriptu.io Now
                </Button>
              </Stack>
            </Card>
          </Sheet>
        </Box>

        {/* Final CTA Section */}
        <Sheet
          sx={{
            background: "linear-gradient(135deg, #2563eb 0%, #7e22ce 100%)",
            color: "white",
            py: 8,
          }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>
            <Stack spacing={6} alignItems="center" textAlign="center">
              <Typography level="h1" fontWeight="bold" sx={{ color: "white", pb: 2.5 }}>
                {`Don't Miss This Limited-Time Opportunity`}
              </Typography>
              <Typography level="h4" sx={{ maxWidth: "600px", opacity: 0.9, color: "white", mt: "0 !important" }}>
                {`Join 2,847+ professionals who've already transformed their LinkedIn presence with Scriptu.io. This
                            lifetime deal won't last forever.`}
              </Typography>

              <Card
                variant="soft"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  p: 3,
                  maxWidth: "400px",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Stack spacing={1} alignItems="center">
                  <Typography level="h3" fontWeight="bold" sx={{ color: "white" }}>
                    ⏰ Limited Time Offer
                  </Typography>
                  <Typography level="h4" sx={{ color: "white" }}>Only 47 licenses remaining</Typography>
                  <Typography level="body-md" sx={{ opacity: 0.8, color: "white" }}>
                    Deal ends in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
                  </Typography>
                </Stack>
              </Card>

              <Button
                size="lg"
                variant="solid"
                endDecorator={<ArrowRight size={24} />}
                sx={{
                  bgcolor: "white",
                  color: "#7e22ce",
                  px: 8,
                  py: 2,
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  boxShadow: "xl",
                  "&:hover": {
                    bgcolor: "#f9fafb",
                  },
                }}
                onClick={() => {
                  const section = document.getElementById("pricing-section");
                  if (section) section.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Get Lifetime Access Now
              </Button>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Shield size={16} />
                  <Typography level="body-sm" sx={{ color: "white" }}>30-Day Money-Back Guarantee</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Lock size={16} />
                  <Typography level="body-sm" sx={{ color: "white" }}>Secure Payment</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Zap size={16} />
                  <Typography level="body-sm" sx={{ color: "white" }}>Instant Access</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Sheet>



        {/* Footer */}
        <Box sx={{ bgcolor: "#111827", color: "white", py: 6 }}>
          <Container maxWidth="xl">
            <Grid container spacing={4}>
              <Grid xs={12} md={6}>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
                        borderRadius: "lg",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography level="title-sm" sx={{ color: "white", fontWeight: "bold" }}>
                        S
                      </Typography>
                    </Box>
                    <Typography level="h4" fontWeight="bold">
                      Scriptu.io
                    </Typography>
                  </Stack>
                  <Typography level="body-md" sx={{ color: "#9ca3af", maxWidth: 400 }}>
                    The AI-powered LinkedIn automation platform trusted by 2,847+ professionals worldwide. Transform your
                    LinkedIn presence today.
                  </Typography>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#374151",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#4b5563",
                      },
                    }}
                  >
                    <Linkedin size={16} />
                  </Box>
                </Stack>
              </Grid>

              <Grid xs={6} md={3}>
                <Stack spacing={2}>
                  <Typography level="body-sm" fontWeight="bold" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                    Product
                  </Typography>
                  <Stack spacing={1}>
                    {["Features", "Pricing", "Reviews", "FAQ"].map((item) => (
                      <Typography
                        key={item}
                        level="body-sm"
                        sx={{
                          color: "#9ca3af",
                          cursor: "pointer",
                          "&:hover": { color: "white" },
                        }}
                        onClick={() => {
                          const section = document.getElementById(item.toLowerCase().replace(" ", "-") + "-section");
                          if (section) section.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
              </Grid>

              <Grid xs={6} md={3}>
                <Stack spacing={2}>
                  <Typography level="body-sm" fontWeight="bold" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                    Support
                  </Typography>
                  <Stack spacing={1}>
                    <Typography
                      level="body-sm"
                      sx={{
                        color: "#9ca3af",
                        cursor: "pointer",
                        "&:hover": { color: "white" },
                      }}
                      onClick={() => {
                        const section = document.getElementById("contact-section");
                        if (section) section.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Contact Us
                    </Typography>

                    <Link href="/privacy" underline="none">
                      <Typography
                        level="body-sm"
                        sx={{
                          color: "#9ca3af",
                          cursor: "pointer",
                          "&:hover": { color: "white" },
                        }}
                      >
                        Privacy Policy
                      </Typography>
                    </Link>

                    <Link href="/terms" underline="none">
                      <Typography
                        level="body-sm"
                        sx={{
                          color: "#9ca3af",
                          cursor: "pointer",
                          "&:hover": { color: "white" },
                        }}
                      >
                        Terms of Service
                      </Typography>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ borderTop: "1px solid #374151", mt: 4, pt: 4 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Typography level="body-sm" sx={{ color: "#9ca3af", textAlign: { xs: "center", md: "left" } }}>
                  © 2025 Scriptu.io. All rights reserved. This is a limited-time lifetime deal offer.
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Shield size={16} />
                    <Typography level="body-sm" sx={{ color: "#9ca3af" }}>
                      Secure & Safe
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Award size={16} />
                    <Typography level="body-sm" sx={{ color: "#9ca3af" }}>
                      LinkedIn Compliant
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Container>
        </Box>
        <GDPRPopup />
      </Box>
    </>
  )
}
